import { entryUrlFromHtml, runningEntryUrl } from './appEntry';

const CHECK_INTERVAL_MS = 30 * 1000;
const MIN_CHECK_INTERVAL_MS = 10 * 1000;
const INACTIVE_BEFORE_RELOAD_MS = 30 * 60 * 1000;
export const STARTUP_VERSION_CHECK_TIMEOUT_MS = 5000;
const UPDATE_ATTEMPT_PREFIX = 'app-update-attempt:';
const AUTO_RELOAD_HISTORY_KEY = 'app-update-auto-reloads';
const MAX_AUTO_RELOADS = 2;
const AUTO_RELOAD_WINDOW_MS = 30 * 60 * 1000;

async function clearOldStaticAssets() {
    let workerScope = null;
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration) {
                workerScope = registration.scope;
                await registration.unregister();
            }
        } catch (error) {
            console.warn('Não foi possível desregistrar o service worker', error);
        }
    }

    if (workerScope && 'caches' in window) {
        try {
            await window.caches.delete(`workbox-precache-v2-${workerScope}`);
        } catch (error) {
            console.warn('Não foi possível limpar os arquivos antigos em cache', error);
        }
    }
}

export function startVersionMonitor({
    fetchDocument = (url, options) => fetch(url, options),
    reload = () => window.location.reload(),
    now = () => Date.now(),
    intervalMs = CHECK_INTERVAL_MS,
    minCheckIntervalMs = MIN_CHECK_INTERVAL_MS,
    initialCheck = true,
    loadedDocumentModified = document.lastModified,
    isFocused = () => !document.hidden && document.hasFocus(),
} = {}) {
    const runningEntry = runningEntryUrl();
    if (!runningEntry) return { check: async () => {}, checkOnStartup: async () => false, cancelStartupCheck: () => {}, stop: () => {} };
    const loadedModifiedAt = Date.parse(loadedDocumentModified);

    let stopped = false;
    let checking = false;
    let currentCheckDone = Promise.resolve();
    let resolveCurrentCheck;
    let reloading = false;
    let startupCheckAllowed = false;
    let returnCheckInProgress = false;
    let returnCheckTimeout;
    let lastCheckAt = -Infinity;
    let candidate = null;
    let confirmations = 0;
    let inactiveSince = isFocused() ? null : now();

    const inactiveLongEnough = () => inactiveSince !== null
        && now() - inactiveSince >= INACTIVE_BEFORE_RELOAD_MS;

    const recentAutoReloads = (at) => {
        const saved = sessionStorage.getItem(AUTO_RELOAD_HISTORY_KEY);
        if (!saved) return [];
        const timestamps = JSON.parse(saved);
        if (!Array.isArray(timestamps) || !timestamps.every(Number.isFinite)) {
            throw new Error('Histórico de recargas automáticas inválido');
        }
        return timestamps.filter((timestamp) => timestamp <= at && at - timestamp < AUTO_RELOAD_WINDOW_MS);
    };

    const reloadVersion = async (version, duringStartup = false) => {
        if (reloading || !version) return;
        const attemptKey = `${UPDATE_ATTEMPT_PREFIX}${version}`;
        try {
            if (sessionStorage.getItem(attemptKey) || recentAutoReloads(now()).length >= MAX_AUTO_RELOADS) return;
        } catch (error) {
            console.warn('Sem armazenamento para limitar a recarga de versão', error);
            return;
        }

        reloading = true;
        await clearOldStaticAssets();
        if (duringStartup && !startupCheckAllowed) {
            reloading = false;
            return;
        }
        try {
            const attemptedAt = now();
            const recent = recentAutoReloads(attemptedAt);
            if (sessionStorage.getItem(attemptKey) || recent.length >= MAX_AUTO_RELOADS) {
                reloading = false;
                return;
            }
            sessionStorage.setItem(AUTO_RELOAD_HISTORY_KEY, JSON.stringify([...recent, attemptedAt]));
            sessionStorage.setItem(attemptKey, String(attemptedAt));
        } catch (error) {
            reloading = false;
            console.warn('Sem armazenamento para limitar a recarga de versão', error);
            return;
        }
        reload();
    };

    const check = async (duringStartup = false) => {
        if (stopped || checking || reloading || (!duringStartup && now() - lastCheckAt < minCheckIntervalMs)) return;
        checking = true;
        currentCheckDone = new Promise((resolve) => { resolveCurrentCheck = resolve; });
        lastCheckAt = now();

        try {
            const indexUrl = new URL('index.html', document.baseURI);
            indexUrl.searchParams.set('version-check', String(Math.floor(now() / CHECK_INTERVAL_MS)));
            const response = await fetchDocument(indexUrl.href, { cache: 'no-store' });
            if (!response.ok) return;

            const nextEntry = entryUrlFromHtml(await response.text(), response.url || indexUrl.href);
            const publishedAt = Date.parse(response.headers?.get('Last-Modified') || '');
            const newerDocument = Number.isFinite(loadedModifiedAt)
                && Number.isFinite(publishedAt)
                && publishedAt > loadedModifiedAt;
            if (!nextEntry || (nextEntry === runningEntry && !newerDocument)) {
                candidate = null;
                confirmations = 0;
                return;
            }

            const nextVersion = newerDocument ? `${nextEntry}@${publishedAt}` : nextEntry;
            if (candidate !== nextVersion) {
                candidate = nextVersion;
                confirmations = 1;
                return;
            }
            confirmations++;
            if (confirmations < 2) return;
            if (duringStartup && startupCheckAllowed) await reloadVersion(nextVersion, true);
            else if (inactiveLongEnough() && !isFocused()) await reloadVersion(nextVersion);
        } catch (error) {
            console.warn('Não foi possível verificar a versão da aplicação', error);
        } finally {
            checking = false;
            resolveCurrentCheck();
        }
    };

    const checkOnStartup = async () => {
        startupCheckAllowed = true;
        try {
            if (checking) await currentCheckDone;
            if (!startupCheckAllowed) return false;
            await check(true);
            if (startupCheckAllowed && candidate && confirmations === 1) await check(true);
            return reloading;
        } finally {
            startupCheckAllowed = false;
        }
    };

    const cancelStartupCheck = () => { startupCheckAllowed = false; };
    const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'input'];
    const onPageShow = (event) => {
        if (!event.persisted || stopped || returnCheckInProgress) return;
        returnCheckInProgress = true;
        interactionEvents.forEach((name) => window.addEventListener(name, cancelStartupCheck, true));
        returnCheckTimeout = window.setTimeout(cancelStartupCheck, STARTUP_VERSION_CHECK_TIMEOUT_MS);
        void checkOnStartup().finally(() => {
            window.clearTimeout(returnCheckTimeout);
            interactionEvents.forEach((name) => window.removeEventListener(name, cancelStartupCheck, true));
            returnCheckInProgress = false;
        });
    };

    const onBlur = () => {
        if (inactiveSince === null) inactiveSince = now();
    };
    const onFocus = () => {
        if (isFocused()) inactiveSince = null;
    };
    const onVisibilityChange = () => {
        if (isFocused()) onFocus();
        else onBlur();
    };
    const timer = window.setInterval(() => {
        if (inactiveSince !== null && !isFocused()) void check();
    }, intervalMs);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (initialCheck) void check();

    return {
        check,
        checkOnStartup,
        cancelStartupCheck,
        stop: () => {
            stopped = true;
            window.clearInterval(timer);
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('pageshow', onPageShow);
            window.clearTimeout(returnCheckTimeout);
            interactionEvents.forEach((name) => window.removeEventListener(name, cancelStartupCheck, true));
            document.removeEventListener('visibilitychange', onVisibilityChange);
        },
    };
}
